import { useEffect, useRef, useCallback } from 'react';
import { useChatActions } from './useChatActions';
import { useChatState } from './useChatState';
import { Message } from '../types';
import io from 'socket.io-client';

interface UseWebSocketConnectionOptions {
  endpoint: string;
  maxRetries?: number;
  baseRetryDelay?: number;
  maxRetryDelay?: number;
  autoConnect?: boolean;
}



/**
 * WebSocket connection hook for real-time voice communication using Socket.IO
 */
export const useWebSocketConnection = ({
  endpoint,
  maxRetries = 3,
  baseRetryDelay = 2000,
  maxRetryDelay = 30000,
  autoConnect = true,
}: UseWebSocketConnectionOptions) => {
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isManuallyClosedRef = useRef(false);
  const connectionAttemptRef = useRef(false);
  const isVoiceSessionActiveRef = useRef(false);

  const { addMessage, setConnectionStatus } = useChatActions();
  const { connectionStatus } = useChatState();

  // Store the latest callback functions in refs to avoid stale closures
  const addMessageRef = useRef(addMessage);
  const setConnectionStatusRef = useRef(setConnectionStatus);

  // Update refs when callbacks change
  useEffect(() => {
    addMessageRef.current = addMessage;
    setConnectionStatusRef.current = setConnectionStatus;
  }, [addMessage, setConnectionStatus]);

  // Calculate exponential backoff delay
  const calculateRetryDelay = useCallback((retryCount: number): number => {
    const delay = baseRetryDelay * Math.pow(2, retryCount);
    return Math.min(delay, maxRetryDelay);
  }, [baseRetryDelay, maxRetryDelay]);

  // Handle incoming Socket.IO messages
  const setupSocketHandlers = useCallback((socket: ReturnType<typeof io>) => {
    socket.on('connect', () => {
      console.log('Socket.IO connected successfully');
      retryCountRef.current = 0;
      connectionAttemptRef.current = false;
      setConnectionStatusRef.current({ status: 'connected' });
    });

    socket.on('disconnect', (reason: string) => {
      console.log('Socket.IO disconnected:', reason);
      connectionAttemptRef.current = false;
      isVoiceSessionActiveRef.current = false;
      
      if (!isManuallyClosedRef.current) {
        setConnectionStatusRef.current({ status: 'disconnected' });
      }
    });

    socket.on('connect_error', (error: Error) => {
      console.error('Socket.IO connection error:', error);
      connectionAttemptRef.current = false;
      setConnectionStatusRef.current({ status: 'disconnected' });
    });

    socket.on('message', (messageData: Message) => {
      const message: Message = {
        ...messageData,
        timestamp: new Date(messageData.timestamp),
      };
      addMessageRef.current(message);
    });

    socket.on('connection', (data: any) => {
      setConnectionStatusRef.current(data);
    });

    socket.on('error', (error: any) => {
      console.error('Socket.IO error:', error);
    });

    socket.on('voice_session_started', (data: any) => {
      console.log('Voice session started:', data);
      isVoiceSessionActiveRef.current = true;
    });

    socket.on('voice_data_received', (data: any) => {
      console.log('Voice data received acknowledgment:', data);
    });

    socket.on('voice_session_ended', (data: any) => {
      console.log('Voice session ended:', data);
      isVoiceSessionActiveRef.current = false;
    });
  }, []);



  // Establish Socket.IO connection
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (connectionAttemptRef.current) {
      console.log('Socket.IO connection attempt already in progress, skipping...');
      return;
    }

    // Don't create a new connection if one is already connecting or connected
    if (socketRef.current && socketRef.current.connected) {
      console.log('Socket.IO connection already exists, skipping...');
      return;
    }

    connectionAttemptRef.current = true;

    // Close existing connection if any
    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
      } catch (error) {
        console.warn('Error closing existing Socket.IO connection:', error);
      }
      socketRef.current = null;
    }

    try {
      console.log('Attempting to establish Socket.IO connection to:', endpoint);
      setConnectionStatusRef.current({ status: 'reconnecting' });

      // Create Socket.IO connection with namespace
      const socket = io(`${endpoint}/api/chat/voice`, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnection: false, // We handle reconnection manually
      });

      socketRef.current = socket;
      setupSocketHandlers(socket);

    } catch (error) {
      console.error('Failed to create Socket.IO connection:', error);
      connectionAttemptRef.current = false;
      setConnectionStatusRef.current({ status: 'disconnected' });
    }
  }, [endpoint, setupSocketHandlers]);

  // Retry connection with exponential backoff
  const retryConnection = useCallback(() => {
    if (isManuallyClosedRef.current || retryCountRef.current >= maxRetries) {
      console.log('Max retries reached or connection manually closed');
      setConnectionStatusRef.current({ status: 'disconnected' });
      return;
    }

    // Don't retry if already connecting or connected
    if (connectionAttemptRef.current || (socketRef.current && socketRef.current.connected)) {
      console.log('Socket.IO connection attempt in progress or already connected, skipping retry...');
      return;
    }

    const delay = calculateRetryDelay(retryCountRef.current);
    retryCountRef.current += 1;

    console.log(`Retrying Socket.IO connection in ${delay}ms (attempt ${retryCountRef.current}/${maxRetries})`);

    retryTimeoutRef.current = setTimeout(() => {
      connect();
    }, delay);
  }, [maxRetries, calculateRetryDelay, connect]);

  // Send Socket.IO message
  const sendMessage = useCallback((eventName: string, data?: any) => {
    if (socketRef.current && socketRef.current.connected) {
      try {
        socketRef.current.emit(eventName, data);
        return true;
      } catch (error) {
        console.error('Failed to send Socket.IO message:', error);
        return false;
      }
    }
    return false;
  }, []);

  // Start voice session
  const startVoiceSession = useCallback(() => {
    if (!isVoiceSessionActiveRef.current) {
      return sendMessage('voice_start');
    }
    return true;
  }, [sendMessage]);

  // Send voice data
  const sendVoiceData = useCallback((audioData: ArrayBuffer | Blob) => {
    if (audioData instanceof Blob) {
      // Convert Blob to ArrayBuffer
      audioData.arrayBuffer().then(buffer => {
        const base64Data = btoa(String.fromCharCode(...new Uint8Array(buffer)));
        return sendMessage('voice_data', { audio: base64Data });
      });
    } else {
      // Convert ArrayBuffer to base64
      const base64Data = btoa(String.fromCharCode(...new Uint8Array(audioData)));
      return sendMessage('voice_data', { audio: base64Data });
    }
    return true;
  }, [sendMessage]);

  // End voice session
  const endVoiceSession = useCallback(() => {
    if (isVoiceSessionActiveRef.current) {
      return sendMessage('voice_end');
    }
    return true;
  }, [sendMessage]);

  // Manually disconnect
  const disconnect = useCallback(() => {
    console.log('Manually disconnecting Socket.IO connection');
    isManuallyClosedRef.current = true;
    connectionAttemptRef.current = false;
    isVoiceSessionActiveRef.current = false;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionStatusRef.current({ status: 'disconnected' });
  }, []);

  // Manually reconnect (reset retry count)
  const reconnect = useCallback(() => {
    console.log('Manually reconnecting Socket.IO connection');
    isManuallyClosedRef.current = false;
    retryCountRef.current = 0;
    connectionAttemptRef.current = false;
    isVoiceSessionActiveRef.current = false;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    connect();
  }, [connect]);

  // Handle connection state changes for auto-retry
  useEffect(() => {
    if (connectionStatus === 'disconnected' &&
      !isManuallyClosedRef.current &&
      !connectionAttemptRef.current) {

      console.log('Socket.IO connection is disconnected, scheduling retry...');

      const retryTimeout = setTimeout(() => {
        console.log('Executing scheduled Socket.IO retry');
        retryConnection();
      }, 3000);

      return () => {
        console.log('Cancelling scheduled Socket.IO retry');
        clearTimeout(retryTimeout);
      };
    }
  }, [connectionStatus, retryConnection]);

  // Initialize connection on mount - only if autoConnect is true
  useEffect(() => {
    console.log('Initializing WebSocket connection hook');
    isManuallyClosedRef.current = false;
    connectionAttemptRef.current = false;
    isVoiceSessionActiveRef.current = false;

    if (autoConnect) {
      console.log('Starting initial WebSocket connection attempt');
      connect();
    } else {
      console.log('WebSocket auto-connect disabled - connection will be manual');
    }

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up Socket.IO connection hook');
      isManuallyClosedRef.current = true;
      connectionAttemptRef.current = false;
      isVoiceSessionActiveRef.current = false;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (socketRef.current) {
        try {
          socketRef.current.disconnect();
        } catch (error) {
          console.warn('Error closing Socket.IO connection on cleanup:', error);
        }
        socketRef.current = null;
      }
    };
  }, [autoConnect, connect]); // Include autoConnect and connect in dependencies

  return {
    connect,
    disconnect,
    reconnect,
    sendMessage,
    startVoiceSession,
    sendVoiceData,
    endVoiceSession,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    isReconnecting: connectionStatus === 'reconnecting',
    isVoiceSessionActive: isVoiceSessionActiveRef.current,
  };
};